import { RegexMatch } from "@/data/enums/router";
import { DocCategories } from "@/data/enums/docs";
import { ISegmentTypes } from "@/data/enums/segments";
import type { Route } from "vue-router";

import CProdsListing from "@/components/app/global/contractProducts/cProdsListing.vue";
import SegmentResultsListing from "@/components/app/admin/segments/segmentResultsListing.vue";
import { ContractStatusCodes } from "@/models/contracts";

import {
  statusParam,
  AllStatusCodesFilter,
  isFromLast30DaysParam,
  IsCategoryParam,
  IsOneTimePurchaseParam,
  CProdFiltersWithoutCategory
} from "@/data/filters/contractProducts";
import {
  DefaultCProdSorters,
  StatusSorter,
  DatePurchasedSorter,
  DateCancelledSorter
} from "@/data/sorters/contractProducts";

import { getDefaultCProdFilters } from "@/router/admin/contractProducts";
import { ListDisplayMode } from "@/data/constants";
import { UserMetaKeys } from "@/data/enums/users";

export default [
  {
    path: "otps",
    name: "adminOneTimePurchases",
    redirect: { name: "adminOneTimePurchasesList" },
    components: {
      default: () => import("@/views/admin/contractProducts/otps/index.vue")
    },
    meta: {
      backTo: "_.one_time_purchases",
      categorySlug: DocCategories.USING_UPMIND,
      docsSlugs: ["product-management"]
    },
    children: [
      {
        path: "list",
        name: "adminOneTimePurchasesList",
        component: {
          name: "AdminOneTimePurchasesList",
          template: "<router-view />"
        },
        redirect: { name: "admin__allOneTimePurchases" },
        children: [
          {
            path: "all",
            name: "admin__allOneTimePurchases",
            component: CProdsListing,
            props: {
              availableSorters: () => [StatusSorter(), DatePurchasedSorter()],
              availableFilters: () => [
                ...getDefaultCProdFilters(),
                AllStatusCodesFilter()
              ],
              routeFilters: () => ({
                ...IsOneTimePurchaseParam()
              })
            }
          },
          {
            path: "fulfilled",
            name: "admin__fulfilledOneTimePurchases",
            component: CProdsListing,
            props: {
              availableSorters: () => [StatusSorter(), DatePurchasedSorter()],
              availableFilters: () => getDefaultCProdFilters(),
              routeFilters: () => ({
                ...IsOneTimePurchaseParam(),
                ...statusParam([ContractStatusCodes.ACTIVE])
              })
            }
          },
          {
            path: "awaiting-activation",
            name: "admin__awaitingActivationOneTimePurchases",
            component: CProdsListing,
            props: {
              availableSorters: () => [StatusSorter(), DatePurchasedSorter()],
              availableFilters: () => getDefaultCProdFilters(),
              routeFilters: () => ({
                ...IsOneTimePurchaseParam(),
                ...statusParam([ContractStatusCodes.AWAITING_ACTIVATION])
              })
            }
          },
          {
            path: "awaiting-payment",
            name: "admin__awaitingPaymentOneTimePurchases",
            component: CProdsListing,
            props: {
              availableSorters: () => [StatusSorter(), DatePurchasedSorter()],
              availableFilters: () => getDefaultCProdFilters(),
              routeFilters: () => ({
                ...IsOneTimePurchaseParam(),
                ...statusParam([ContractStatusCodes.PENDING])
              })
            }
          },
          {
            path: "suspended",
            name: "admin__suspendedOneTimePurchases",
            component: CProdsListing,
            props: {
              availableSorters: () => [StatusSorter(), DatePurchasedSorter()],
              availableFilters: () => getDefaultCProdFilters(),
              routeFilters: () => ({
                ...IsOneTimePurchaseParam(),
                ...statusParam([ContractStatusCodes.SUSPENDED])
              })
            }
          },
          {
            path: "cancelled",
            name: "admin__cancelledOneTimePurchases",
            component: CProdsListing,
            props: {
              availableSorters: () => [
                StatusSorter(),
                DateCancelledSorter(),
                DatePurchasedSorter()
              ],
              availableFilters: () => getDefaultCProdFilters(),
              routeFilters: () => ({
                ...IsOneTimePurchaseParam(),
                ...statusParam([ContractStatusCodes.CANCELLED])
              })
            }
          },
          {
            path: "lapsed",
            name: "admin__lapsedOneTimePurchases",
            component: CProdsListing,
            props: {
              availableSorters: () => [StatusSorter(), DatePurchasedSorter()],
              availableFilters: () => getDefaultCProdFilters(),
              routeFilters: () => ({
                ...IsOneTimePurchaseParam(),
                ...statusParam([ContractStatusCodes.CLOSED])
              })
            }
          },
          {
            path: "last-30-days",
            name: "admin__fromLast30DaysOneTimePurchases",
            component: CProdsListing,
            props: {
              availableSorters: () => [StatusSorter(), DatePurchasedSorter()],
              availableFilters: () => [
                ...getDefaultCProdFilters(),
                AllStatusCodesFilter()
              ],
              routeFilters: () => ({
                ...IsOneTimePurchaseParam(),
                ...isFromLast30DaysParam()
              })
            }
          }
        ]
      },
      {
        path: `category/:categoryId(${RegexMatch.UUID})`,
        name: "admin__allOTPsCategoryProducts",
        component: CProdsListing,
        props: {
          availableSorters: () => [StatusSorter(), DatePurchasedSorter()],
          availableFilters: () => [
            ...CProdFiltersWithoutCategory(),
            AllStatusCodesFilter()
          ],
          routeFilters: vm => ({
            ...IsOneTimePurchaseParam(),
            ...IsCategoryParam(vm)
          })
        }
      },
      {
        path: `segment/:segmentId(${RegexMatch.UUID})`,
        name: "adminOneTimePurchasesSegmentList",
        component: {
          name: "AdminOneTimePurchasesSegmentList",
          template: "<router-view />"
        },
        redirect: { name: "admin__oneTimePurchasesSegment" },
        children: [
          {
            path: "",
            name: "admin__oneTimePurchasesSegment",
            component: SegmentResultsListing,
            props: (route: Route) => ({
              defaultDisplayMode: ListDisplayMode.GRID,
              displayModeStoragePath: UserMetaKeys.UI_CPRODS_VIEW,
              segmentTypeHint: ISegmentTypes.CONTRACTS_PRODUCTS_SUBSCRIPTION,
              segmentId: route.params.segmentId,
              newSegmentRouteName: "adminOneTimePurchasesSegmentList",
              availableSorters: DefaultCProdSorters,
              availableFilters: () => [
                ...getDefaultCProdFilters(),
                AllStatusCodesFilter()
              ]
            })
          }
        ]
      }
    ]
  }
];
